import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from "react";
import { db } from "services/firebase";
import { collection, onSnapshot, doc, query, writeBatch, getDocs, updateDoc } from "firebase/firestore";
import { useAuth } from "./AuthContext";

interface NotificationItem {
  id: string;
  label: string;
  checked: boolean;
  notified: boolean;
}

interface NotificationContextType {
  notifications: NotificationItem[];
  updateNotificationStatus: (id: string, checked: boolean) => Promise<void>;
  resetSingleNotification: (id: string) => Promise<void>;
  resetAllNotifications: () => Promise<void>;
}

const defaultNotifications: NotificationItem[] = [
  { id: "switch1", label: "Alerta de novos itens", checked: true, notified: false },
  // { id: "switch2", label: "Receber SMS de novos itens", checked: false, notified: false },
  { id: "switch3", label: "Alerta para exportar relatório", checked: true, notified: false },
  // { id: "switch4", label: "Receber SMS para exportar relatório", checked: false, notified: false },
  { id: "switch5", label: "Sugestões da Assistente AI", checked: true, notified: false },
  { id: "switch6", label: "Alerta para Dispositivos Offline", checked: true, notified: false },
  // { id: "switch7", label: "Receber SMS para Dispositivos Offline", checked: false, notified: false },
  // { id: "switch8", label: "Alerta para renovar assinatura", checked: true, notified: false },
  // { id: "switch9", label: "Receber SMS para renovar assinatura", checked: false, notified: false },
  { id: "switch10", label: "Sons de notificação", checked: true, notified: false },
  { id: "switch11", label: "Alerta para Sobra Limpa", checked: true, notified: false },
  // { id: "switch12", label: "Receber SMS para Sobra Limpa", checked: false, notified: false },
];

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);

  const getCollectionRef = useCallback((collectionName: string) => {
    if (!user?.uid) return null;
    return collection(db, `clientes/${user.uid}/${collectionName}`);
  }, [user?.uid]);

  const getDocRef = useCallback((collectionName: string, id: string) => {
    if (!user?.uid) return null;
    return doc(db, `clientes/${user.uid}/${collectionName}`, id);
  }, [user?.uid]);

  useEffect(() => {
    if (!user) {
      setNotifications(defaultNotifications);
      return;
    }

    const notificationsCollectionRef = getCollectionRef("notifications");
    if (!notificationsCollectionRef) return;

    const unsubscribe = onSnapshot(query(notificationsCollectionRef), (snapshot) => {
      if (snapshot.empty) {
        const batch = writeBatch(db);
        for (const notif of defaultNotifications) {
          const docRef = getDocRef("notifications", notif.id);
          if (docRef) batch.set(docRef, notif);
        }
        batch.commit();
        setNotifications(defaultNotifications);
      } else {
        const fetched = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        })) as NotificationItem[];

        const mergedNotifications = defaultNotifications.map(defaultNotif => {
          const found = fetched.find(f => f.id === defaultNotif.id);
          return found ? { ...defaultNotif, ...found } : defaultNotif;
        });
        setNotifications(mergedNotifications);
      }
    }, (error) => {
      console.error("Erro ao ouvir notificações: ", error);
      setNotifications(defaultNotifications);
    });

    return () => unsubscribe();
  }, [user, getCollectionRef, getDocRef]);

  const hasPhoneNumber = useCallback(async (): Promise<boolean> => {
    const assinaturaCollectionRef = getCollectionRef("assinatura");
    if (!assinaturaCollectionRef) return false;
    try {
      const q = query(assinaturaCollectionRef);
      const querySnapshot = await getDocs(q);
      if (querySnapshot.empty) return false;
      const assinaturaData = querySnapshot.docs[0].data();
      return !!assinaturaData.Numero;
    } catch (error) {
      console.error("Erro ao verificar número de celular:", error);
      return false;
    }
  }, [getCollectionRef]);

  const updateNotificationStatus = useCallback(async (id: string, checked: boolean) => {
    const notification = notifications.find(n => n.id === id);
    if (notification && notification.label.toLowerCase().includes("sms") && checked) {
      const phoneExists = await hasPhoneNumber();
      if (!phoneExists) {
        alert("Cadastre um número de celular primeiro para ativar notificações por SMS.");
        return;
      }
    }

    const notificationDocRef = getDocRef("notifications", id);
    if (!notificationDocRef) return;

    try {
      await updateDoc(notificationDocRef, { checked: checked });
    } catch (error) {
      console.error("Erro ao atualizar status da notificação: ", error);
    }
  }, [notifications, hasPhoneNumber, getDocRef]);

  const resetSingleNotification = async (id: string) => {
    const notificationDocRef = getDocRef("notifications", id);
    if (!notificationDocRef) return;
    try {
      await updateDoc(notificationDocRef, { notified: false });
    } catch (error) {
      console.error("Erro ao resetar notificação:", error);
    }
  };

  const resetAllNotifications = async () => {
    const notificationsCollectionRef = getCollectionRef("notifications");
    if (!notificationsCollectionRef) return;
    try {
      const batch = writeBatch(db);
      notifications.forEach(notif => {
        if (notif.notified) {
          const docRef = getDocRef("notifications", notif.id);
          if (docRef) batch.update(docRef, { notified: false });
        }
      });
      await batch.commit();
    } catch (error) {
      console.error("Erro ao resetar todas as notificações:", error);
    }
  };

  return (
    <NotificationContext.Provider value={{ notifications, updateNotificationStatus, resetSingleNotification, resetAllNotifications }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotifications precisa estar dentro de NotificationProvider");
  }
  return context;
};
